import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BlueInput, BlueTextarea } from "@/components/BlueField";
import { PhotoUploader } from "@/components/PhotoUploader";
import { SignaturePad } from "@/components/SignaturePad";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, ScrollText, FileText, Info, Lock, WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { draftKey, loadDraft, saveDraft, clearDraft, enqueue, uploadLocalPhotos, localPhotoIds, deleteLocalPhotos, getQueueItem, updateQueueItem, removeQueueItem } from "@/lib/offline";

function Section({ title, icon: Icon, children, right }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
        <h3 className="flex items-center gap-2 font-head text-base font-bold text-slate-900 dark:text-white">
          {Icon && <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400"><Icon className="h-4 w-4" /></span>}
          {title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}

const emptyRow = (cols) => Object.fromEntries(cols.map((c) => [c, ""]));

const WORK_COLS = [
  ["rabotage", "Rabotage"], ["enrobes", "Enrobés"], ["sh", "SH"],
  ["joints", "Joints Ouvrages"], ["itpc", "ITPC"], ["autres", "Autres"],
];

// Grid: fixed row labels × work-type columns of BlueInputs
function WorkTypeGrid({ rowDefs, cols, getVal, onChange, testPrefix }) {
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <div className="min-w-[640px] space-y-2">
        <div className="grid gap-2" style={{ gridTemplateColumns: `140px repeat(${cols.length}, 1fr)` }}>
          <div />
          {cols.map(([k, lbl]) => (
            <div key={k} className="px-1 text-center text-xs font-semibold text-slate-500 dark:text-slate-400">{lbl}</div>
          ))}
        </div>
        {rowDefs.map(([rk, rlabel]) => (
          <div key={rk} className="grid items-center gap-2" style={{ gridTemplateColumns: `140px repeat(${cols.length}, 1fr)` }}>
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">{rlabel}</div>
            {cols.map(([ck]) => (
              <input key={ck} data-testid={`${testPrefix}-${rk}-${ck}`} value={getVal(rk, ck) ?? ""}
                onChange={(e) => onChange(rk, ck, e.target.value)}
                className="blue-field h-10 w-full min-w-0 rounded-lg px-2 text-center text-sm" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


function RepeatRows({ rows, cols, labels, onChange, testPrefix }) {
  const add = () => onChange([...(rows || []), emptyRow(cols)]);
  const upd = (i, key, v) => { const n = [...rows]; n[i] = { ...n[i], [key]: v }; onChange(n); };
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {(rows || []).map((r, i) => (
        <div key={i} data-testid={`${testPrefix}-row-${i}`}
          className="relative grid gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))" }}>
          {cols.map((c) => (
            <BlueInput key={c} label={labels[c]} value={r[c]} testId={`${testPrefix}-${c}-${i}`}
              onChange={(v) => upd(i, c, v)} />
          ))}
          <button type="button" onClick={() => del(i)} data-testid={`${testPrefix}-del-${i}`}
            className="absolute -right-2 -top-2 rounded-full bg-red-600 p-1.5 text-white shadow hover:bg-red-700">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid={`${testPrefix}-add`}
        className="h-10 w-full gap-2 border-dashed">
        <Plus className="h-4 w-4" /> Ajouter une ligne
      </Button>
    </div>
  );
}

const blank = {
  type: "pv_liberation",
  status: "en_cours",
  header: { autoroute: "", section: "", pr: "", activite: "", date_travaux: "", date_redaction: "", diffusion: "" },
  data: {},
  photos: [],
  signatures: {
    entreprise: { signature: "", name: "", date: "" },
    iri: { signature: "", name: "", date: "" },
    district: { signature: "", name: "", date: "" },
  },
};

export default function InterventionForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { settings, user } = useAuth();
  const editing = !!id;
  const pendingQid = !editing ? sp.get("pending") : null;
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    if (editing) {
      api.get(`/interventions/${id}`).then((r) => {
        const draft = loadDraft(draftKey(id, r.data.type));
        if (draft?.data) { setF({ ...draft.data, folder_id: r.data.folder_id ?? null }); toast.info("Brouillon local restauré"); }
        else setF(r.data);
      }).catch(() => {
        toast.error("Intervention introuvable"); navigate("/interventions");
      });
    } else if (pendingQid) {
      const item = getQueueItem(pendingQid);
      if (item) setF(item.payload);
      else { toast.info("Ce bon a déjà été synchronisé"); navigate("/interventions"); }
    } else {
      const t = sp.get("type") === "journal_chantier" ? "journal_chantier" : "pv_liberation";
      const folderId = sp.get("dossier") || null;
      const draft = loadDraft(draftKey(null, t));
      if (draft?.data) { setF({ ...draft.data, folder_id: folderId }); toast.info("Brouillon local restauré"); return; }
      setF({
        ...JSON.parse(JSON.stringify(blank)),
        type: t,
        folder_id: folderId,
        header: {
          ...blank.header,
          autoroute: settings?.default_autoroute || "",
          section: settings?.default_section || "",
          activite: settings?.default_activite || "",
        },
        signatures: {
          entreprise: { signature: "", name: settings?.parties?.entreprise || "", date: "" },
          iri: { signature: "", name: settings?.parties?.iri || "", date: "" },
          district: { signature: "", name: settings?.parties?.district || "", date: "" },
        },
      });
    }
    // eslint-disable-next-line
  }, [id]);

  // Auto-save draft locally (debounced)
  useEffect(() => {
    if (!f) return;
    if (pendingQid) {
      const t = setTimeout(() => { if (getQueueItem(pendingQid)) updateQueueItem(pendingQid, f); }, 500);
      return () => clearTimeout(t);
    }
    const dk = draftKey(id, f.type);
    const t = setTimeout(() => saveDraft(dk, f), 500);
    return () => clearTimeout(t);
  }, [f, id, pendingQid]);

  if (!f) return <div className="py-16 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const setHeader = (k, v) => setF((p) => ({ ...p, header: { ...p.header, [k]: v } }));
  const setData = (k, v) => setF((p) => ({ ...p, data: { ...p.data, [k]: v } }));
  const setDataNested = (grp, k, v) => setF((p) => ({ ...p, data: { ...p.data, [grp]: { ...(p.data[grp] || {}), [k]: v } } }));
  const setData2 = (grp, sub, k, v) => setF((p) => ({ ...p, data: { ...p.data, [grp]: { ...(p.data[grp] || {}), [sub]: { ...((p.data[grp] || {})[sub] || {}), [k]: v } } } }));
  const setSig = (party, k, v) => setF((p) => ({ ...p, signatures: { ...p.signatures, [party]: { ...(p.signatures[party] || {}), [k]: v, ...(k === "signature" ? { signed_at: v ? new Date().toLocaleString("fr-FR") : null } : {}) } } }));

  const folderHome = `/interventions?dossier=${f?.folder_id || "none"}`;

  const queueOffline = (method, url, payload) => {
    if (pendingQid && getQueueItem(pendingQid)) updateQueueItem(pendingQid, payload);
    else enqueue({ method, url, payload });
  };

  const save = async (status, lock) => {
    if (pendingQid && !getQueueItem(pendingQid)) {
      toast.info("Ce bon a déjà été synchronisé : retrouvez-le dans son dossier");
      navigate(folderHome);
      return;
    }
    setSaving(true);
    const payload = { ...f, status: status || f.status, title: f.title || "", locked: lock !== undefined ? lock : !!f.locked };
    const dk = draftKey(id, f.type);
    const method = editing ? "put" : "post";
    const url = editing ? `/interventions/${id}` : "/interventions";
    const tmpFolder = String(payload.folder_id || "").startsWith("tmp_");
    if ((typeof navigator !== "undefined" && !navigator.onLine) || tmpFolder) {
      queueOffline(method, url, payload);
      clearDraft(dk);
      setSaving(false);
      toast.success("Hors ligne : bon enregistré sur l'appareil, il sera synchronisé au retour du réseau");
      navigate(folderHome);
      return;
    }
    try {
      let res;
      const ids = localPhotoIds(payload.photos);
      const body = ids.length ? { ...payload, photos: await uploadLocalPhotos(api, payload.photos) } : payload;
      if (editing) res = await api.put(url, body);
      else res = await api.post(url, body);
      await deleteLocalPhotos(ids);
      if (pendingQid) removeQueueItem(pendingQid);
      clearDraft(dk);
      toast.success("Bon d'intervention enregistré");
      navigate(`/interventions/${res.data.id}`);
    } catch (e) {
      if (!e.response) {
        queueOffline(method, url, payload);
        clearDraft(dk);
        toast.success("Réseau indisponible : bon enregistré sur l'appareil, synchronisation automatique au retour du réseau");
        navigate(folderHome);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const isPv = f.type === "pv_liberation";
  const d = f.data || {};

  return (
    <div className="mx-auto max-w-4xl space-y-5 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn" className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                {isPv ? <ScrollText className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </span>
              <h1 className="font-head text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                {isPv ? "PV de libération des emprises" : "Journal de chantier"}
              </h1>
            </div>
            {editing && <p className="mt-1 font-mono text-xs font-semibold text-blue-600">{f.reference}</p>}
          </div>
        </div>
        <StatusBadge status={f.status} />
      </div>

      {!online && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300" data-testid="offline-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <span>Hors ligne — vos saisies sont enregistrées sur l'appareil et seront synchronisées automatiquement au retour du réseau.</span>
        </div>
      )}

      {f.locked && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" data-testid="locked-banner">
          <Lock className="h-4 w-4 shrink-0" />
          <span>Document verrouillé{f.locked_at ? ` (${f.locked_at})` : ""}. {user?.role === "admin" ? "Modification administrateur autorisée." : "Modification impossible après signature."}</span>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
        <Info className="h-4 w-4 shrink-0" />
        <span>Les champs <span className="font-mono font-semibold">modifiables (script) apparaissent en bleu</span>.</span>
      </div>

      {/* Type selector (create only) */}
      {!editing && (
        <Section title="Type de trame">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { v: "pv_liberation", t: "PV de libération des emprises", i: ScrollText },
              { v: "journal_chantier", t: "Journal de chantier", i: FileText },
            ].map((o) => (
              <button key={o.v} type="button" data-testid={`type-${o.v}`}
                onClick={() => setF((p) => ({ ...p, type: o.v, data: {} }))}
                className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                  f.type === o.v ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : "border-slate-200 hover:border-slate-300 dark:border-slate-700"}`}>
                <o.i className={`h-6 w-6 ${f.type === o.v ? "text-amber-600" : "text-slate-400"}`} />
                <span className="font-semibold text-slate-800 dark:text-slate-100">{o.t}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Header */}
      <Section title="En-tête & informations chantier" icon={Info}>
        <div className="grid gap-3 sm:grid-cols-2">
          <BlueInput label="Autoroute / Projet" value={f.header.autoroute} onChange={(v) => setHeader("autoroute", v)} testId="h-autoroute" />
          <BlueInput label="Section" value={f.header.section} onChange={(v) => setHeader("section", v)} testId="h-section" />
          <BlueInput label="PR / Sens" value={f.header.pr} onChange={(v) => setHeader("pr", v)} testId="h-pr" />
          <BlueInput label="Activité / Objet" value={f.header.activite} onChange={(v) => setHeader("activite", v)} testId="h-activite" />
          <BlueInput label="Date des travaux" value={f.header.date_travaux} onChange={(v) => setHeader("date_travaux", v)} testId="h-datetravaux" placeholder="Nuit du 09 au 10 sept. 2026" />
          <BlueInput label="Date de rédaction" value={f.header.date_redaction} onChange={(v) => setHeader("date_redaction", v)} testId="h-dateredaction" />
          <BlueInput label="Diffusion" value={f.header.diffusion} onChange={(v) => setHeader("diffusion", v)} testId="h-diffusion" />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-500">Titre du bon (optionnel)</span>
            <Input value={f.title || ""} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} data-testid="f-title" className="h-11" placeholder="Généré automatiquement si vide" />
          </label>
        </div>
      </Section>

      {isPv ? (
        <>
          <Section title="Travaux libérés" icon={ScrollText}>
            <RepeatRows rows={d.travaux} cols={["nature", "localisation", "observations"]}
              labels={{ nature: "Travaux exécuté / Nature", localisation: "Localisation", observations: "Observations" }}
              onChange={(v) => setData("travaux", v)} testPrefix="pv-travaux" />
          </Section>
          <Section title="Horaires de sortie / restitution">
            <RepeatRows rows={d.horaires} cols={["entreprise", "heure_sortie", "heure_prevue", "heure_restitution", "observations"]}
              labels={{ entreprise: "Entreprise", heure_sortie: "Heure de sortie du chantier", heure_prevue: "Heure prévue au marché", heure_restitution: "Heure de restitution à l'exploitant", observations: "Observations" }}
              onChange={(v) => setData("horaires", v)} testPrefix="pv-horaires" />
          </Section>
          <Section title="Incidents / Détails particulier / Observations">
            <BlueTextarea value={d.incidents} onChange={(v) => setData("incidents", v)} rows={4} testId="pv-incidents"
              placeholder="Ex. Accident de la circulation en sens 1 à 4h45 au 532+600..." />
          </Section>
        </>
      ) : (
        <>
          <Section title="Travaux exécuté / Nature" icon={FileText}>
            <RepeatRows rows={d.travaux} cols={["nature", "localisation"]}
              labels={{ nature: "Travaux exécuté / Nature", localisation: "Localisation" }}
              onChange={(v) => setData("travaux", v)} testPrefix="jc-travaux" />
          </Section>
          <Section title="Conditions météorologiques constatées">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <BlueInput label="Température" value={d.meteo?.temperature} onChange={(v) => setDataNested("meteo", "temperature", v)} testId="jc-meteo-temperature" />
              <BlueInput label="Vent" value={d.meteo?.vent} onChange={(v) => setDataNested("meteo", "vent", v)} testId="jc-meteo-vent" />
              <BlueInput label="Pluie" value={d.meteo?.pluie} onChange={(v) => setDataNested("meteo", "pluie", v)} testId="jc-meteo-pluie" />
              <BlueInput label="Autres" value={d.meteo?.autres} onChange={(v) => setDataNested("meteo", "autres", v)} testId="jc-meteo-autres" />
              <BlueInput label="Observations" value={d.meteo?.observations} onChange={(v) => setDataNested("meteo", "observations", v)} testId="jc-meteo-obs" />
            </div>
          </Section>
          <Section title="Horaires d'entrée / sortie du chantier">
            <div className="grid gap-3 sm:grid-cols-3">
              <BlueTextarea label="Heure d'entrée sur le chantier" value={d.entree_sortie?.entree} onChange={(v) => setDataNested("entree_sortie", "entree", v)} testId="jc-es-entree" placeholder="EUROVIA 21h40&#10;AXIMUM 21h40" />
              <BlueTextarea label="Heure de sortie du chantier" value={d.entree_sortie?.sortie} onChange={(v) => setDataNested("entree_sortie", "sortie", v)} testId="jc-es-sortie" placeholder="EUROVIA 05h00&#10;AXIMUM 05h00" />
              <BlueTextarea label="Observations" value={d.entree_sortie?.observations} onChange={(v) => setDataNested("entree_sortie", "observations", v)} testId="jc-es-obs" />
            </div>
          </Section>
          <Section title="Linéaire / Zone traitée — prévu vs réalisé">
            <RepeatRows rows={d.lineaire} cols={["prevu", "realise", "observations"]}
              labels={{ prevu: "Prévue au planning", realise: "Réalisée (PK début et fin)", observations: "Observations" }}
              onChange={(v) => setData("lineaire", v)} testPrefix="jc-lineaire" />
          </Section>
          <Section title="Horaires par type de travaux">
            <WorkTypeGrid
              rowDefs={[["demarrage", "Heure de démarrage"], ["fin", "Heure de fin"]]}
              cols={WORK_COLS}
              getVal={(r, c) => d.horaires_travaux?.[r]?.[c]}
              onChange={(r, c, v) => setData2("horaires_travaux", r, c, v)}
              testPrefix="jc-ht" />
          </Section>
          <Section title="Tonnage, dosage & surfaces">
            <div className="space-y-4">
              <div className="space-y-3 rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                <p className="text-xs font-semibold uppercase text-slate-400">Enrobé</p>
                <div className="grid gap-3 sm:grid-cols-4">
                  <BlueInput label="Tonnage appliqué" value={d.tonnage?.bbtm_tonnage} onChange={(v) => setDataNested("tonnage", "bbtm_tonnage", v)} testId="jc-bbtm-tonnage" />
                  <BlueInput label="Dosage (kg/m²)" value={d.tonnage?.bbtm_dosage} onChange={(v) => setDataNested("tonnage", "bbtm_dosage", v)} testId="jc-bbtm-dosage" />
                  <BlueInput label="Surface d'application" value={d.tonnage?.bbtm_surface} onChange={(v) => setDataNested("tonnage", "bbtm_surface", v)} testId="jc-bbtm-surface" />
                  <BlueInput label="Conforme O/N" value={d.tonnage?.bbtm_conf} onChange={(v) => setDataNested("tonnage", "bbtm_conf", v)} testId="jc-bbtm-conf" />
                </div>
              </div>
              <div className="space-y-3 rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                <p className="text-xs font-semibold uppercase text-slate-400">Autre</p>
                <div className="grid gap-3 sm:grid-cols-4">
                  <BlueInput label="Tonnage appliqué" value={d.tonnage?.autre_tonnage} onChange={(v) => setDataNested("tonnage", "autre_tonnage", v)} testId="jc-autre-tonnage" />
                  <BlueInput label="Dosage (kg/m²)" value={d.tonnage?.autre_dosage} onChange={(v) => setDataNested("tonnage", "autre_dosage", v)} testId="jc-autre-dosage" />
                  <BlueInput label="Surface d'application" value={d.tonnage?.autre_surface} onChange={(v) => setDataNested("tonnage", "autre_surface", v)} testId="jc-autre-surface" />
                  <BlueInput label="Conforme O/N" value={d.tonnage?.autre_conf} onChange={(v) => setDataNested("tonnage", "autre_conf", v)} testId="jc-autre-conf" />
                </div>
              </div>
            </div>
          </Section>
          <Section title="Moyens matériels">
            <div className="grid gap-3 sm:grid-cols-3">
              <BlueTextarea label="Moyens matériels - Atelier enrobés" value={d.materiels?.atelier_enrobes} onChange={(v) => setDataNested("materiels", "atelier_enrobes", v)} testId="jc-mat-enrobes" />
              <BlueTextarea label="Moyens matériels - Atelier SH" value={d.materiels?.atelier_sh} onChange={(v) => setDataNested("materiels", "atelier_sh", v)} testId="jc-mat-sh" />
              <BlueTextarea label="Autres" value={d.materiels?.autres} onChange={(v) => setDataNested("materiels", "autres", v)} testId="jc-mat-autres" />
            </div>
          </Section>
          <Section title="Conformité d'application">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Conformité d'application planning OUI/NON</p>
                <BlueInput label="OUI / NON" value={d.conformite_planning?.valeur} onChange={(v) => setDataNested("conformite_planning", "valeur", v)} testId="jc-confp-val" />
                <BlueTextarea label="Observations" value={d.conformite_planning?.observations} onChange={(v) => setDataNested("conformite_planning", "observations", v)} testId="jc-confp-obs" />
              </div>
              <div className="space-y-2 rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Conformité d'application technique OUI/NON</p>
                <BlueInput label="OUI / NON" value={d.conformite_technique?.valeur} onChange={(v) => setDataNested("conformite_technique", "valeur", v)} testId="jc-conft-val" />
                <BlueTextarea label="Observations" value={d.conformite_technique?.observations} onChange={(v) => setDataNested("conformite_technique", "observations", v)} testId="jc-conft-obs" />
              </div>
            </div>
          </Section>
          <Section title="Observations">
            <div className="grid gap-3">
              <BlueTextarea label="Observations hygiène sécurité" value={d.hygiene_securite} onChange={(v) => setData("hygiene_securite", v)} testId="jc-hse" />
              <BlueTextarea label="Incidents / Détails particulier / Observations faites et prescriptions imposées à l'Entreprise" value={d.incidents} onChange={(v) => setData("incidents", v)} testId="jc-incidents" />
              <BlueTextarea label="Observations environnement" value={d.environnement} onChange={(v) => setData("environnement", v)} testId="jc-env" />
            </div>
          </Section>
          <Section title="Détails de la réalisation">
            <WorkTypeGrid
              rowDefs={[["arret", "Durée/cause arrêt de chantier"], ["modifications", "Modifications en cours d'avancement"]]}
              cols={[["rabotage", "Rabotage"], ["enrobes", "Enrobés"], ["sh", "SH"], ["autres", "Autres"]]}
              getVal={(r, c) => d.details_realisation?.[r]?.[c]}
              onChange={(r, c, v) => setData2("details_realisation", r, c, v)}
              testPrefix="jc-dr" />
          </Section>
        </>
      )}

      <Section title="Galerie photos du chantier">
        <PhotoUploader photos={f.photos} onChange={(v) => setF((p) => ({ ...p, photos: v }))} />
      </Section>

      <Section title="Signatures des parties">
        <div className={`grid gap-4 ${isPv ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
          {(isPv
            ? [{ key: "entreprise", label: "L'Entreprise" }, { key: "iri", label: "IR Ingénierie" }, { key: "district", label: "District" }]
            : [{ key: "entreprise", label: "L'Entreprise" }, { key: "iri", label: "IR Ingénierie" }]
          ).map((pt) => (
            <SignaturePad key={pt.key} title={pt.label} testKey={pt.key}
              signature={f.signatures[pt.key]?.signature}
              onSignature={(v) => setSig(pt.key, "signature", v)}
              nameValue={f.signatures[pt.key]?.name}
              onName={(v) => setSig(pt.key, "name", v)}
              dateValue={f.signatures[pt.key]?.date}
              onDate={(v) => setSig(pt.key, "date", v)} />
          ))}
        </div>
      </Section>

      {/* Sticky actions */}
      <div className="pb-safe fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 lg:pl-64">
        <div className="mx-auto flex max-w-4xl items-center justify-end gap-3">
          <div className="mr-auto hidden items-center gap-2 sm:flex">
            <span className="text-xs text-slate-500">Statut :</span>
            <Select value={f.status} onValueChange={(v) => setF((p) => ({ ...p, status: v }))}>
              <SelectTrigger className="h-10 w-36" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => save(f.status)} disabled={saving} data-testid="save-draft-btn" className="h-11 gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </Button>
          <Button onClick={() => save("termine")} disabled={saving} data-testid="save-terminer-btn"
            className="h-11 gap-2 bg-emerald-600 font-semibold text-white hover:bg-emerald-500">
            Terminer
          </Button>
          {f.signatures?.entreprise?.signature && !f.locked && (
            <Button onClick={() => save("termine", true)} disabled={saving} data-testid="lock-btn"
              className="h-11 gap-2 bg-slate-900 font-semibold text-white hover:bg-slate-800">
              <Lock className="h-4 w-4" /> Verrouiller
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
